import React, { useState, useEffect, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { useDropzone } from 'react-dropzone'

import { UploadCloud, RefreshCw, Bell, Plus, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table'

import {
  UPLOADING,
  FAILED,
  QUEUED,
  requestUploads,
  requestUploadAccountChange,
  requestHandleNewFiles,
  requestUpdateNotifications
} from '../../stores/upload'

import S3UploaderProgress from '../../components/S3UploaderProgress'

import './index.css'

function UploadDropzone({ disabled, onDrop }) {
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    multiple: true,
    disabled,
    noClick: false,
  })

  return (
    <div
      {...getRootProps()}
      className={cn(
        'rounded-lg border-2 border-dashed p-8 text-center transition-colors',
        disabled
          ? 'cursor-not-allowed border-border bg-secondary/40 opacity-60'
          : 'cursor-pointer border-border bg-background hover:border-primary hover:bg-accent/40',
        isDragReject && 'border-destructive',
        isDragActive && !isDragReject && 'border-primary bg-accent/60'
      )}
    >
      <input {...getInputProps()} />
      <UploadCloud className={cn('mx-auto mb-2 h-10 w-10', disabled ? 'text-muted-foreground' : 'text-primary')} />
      <p className={cn('font-medium', disabled ? 'text-muted-foreground' : 'text-foreground')}>
        {isDragActive ? 'Drop files here…' : 'Drag & drop files here, or click to select'}
      </p>
      <p className="text-xs text-muted-foreground">
        {disabled ? 'Select an account above to enable uploads' : 'Multiple files supported'}
      </p>
    </div>
  )
}

function NotificationsModal({
  open,
  onClose,
  initialEmails,
  loading,
  hasError,
  error,
  onSave,
}) {
  const [localEmails, setLocalEmails] = useState([])
  const [inputVal, setInputVal] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    if (open) {
      setLocalEmails(initialEmails ? [...initialEmails] : [])
      setInputVal('')
    }
  }, [open, initialEmails])

  const addEmail = useCallback(() => {
    const trimmed = inputVal.trim().toLowerCase()
    if (trimmed && !localEmails.includes(trimmed)) {
      setLocalEmails(prev => [...prev, trimmed])
    }
    setInputVal('')
    inputRef.current?.focus()
  }, [inputVal, localEmails])

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addEmail()
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Notifications</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Add email addresses to notify when uploads complete or fail.
        </p>

        {hasError && <Alert variant="error">{error}</Alert>}

        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="notif-email">Email address</Label>
            <Input
              id="notif-email"
              ref={inputRef}
              type="email"
              value={inputVal}
              onChange={e => setInputVal(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="name@example.com"
            />
          </div>
          <Button variant="outline" onClick={addEmail} disabled={!inputVal.trim()} className="shrink-0">
            <Plus className="h-4 w-4" /> Add Email
          </Button>
        </div>

        {localEmails.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {localEmails.map(email => (
              <Badge key={email} variant="secondary" className="gap-1 pr-1">
                {email}
                <button
                  onClick={() => setLocalEmails(prev => prev.filter(e => e !== email))}
                  className="rounded-full p-0.5 hover:bg-foreground/10"
                  aria-label={`Remove ${email}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm italic text-muted-foreground">No email addresses added yet.</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={() => onSave(localEmails)} disabled={loading}>
            {loading && <Spinner size={16} className="text-primary-foreground" />}
            {loading ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Upload({
  fetched,
  recentFiles,
  uploads,
  uploadAccounts,
  selectedUploadAccount,
  accessToMultipleS3Uploaders,
  accessToAtleastOneS3Uploader,
  uploadEmailNotificationsList,
  uploadEmailNotificationsLoading,
  uploadEmailNotificationsHasError,
  uploadEmailNotificationsError,
  loading,
  error,
  hasError,
  requestUploads,
  requestUploadAccountChange,
  requestHandleNewFiles,
  requestUpdateNotifications,
}) {
  const [notifOpen, setNotifOpen] = useState(false)

  useEffect(() => {
    const handler = event => {
      let pending = 0, failed = 0
      for (const [, value] of Object.entries(uploads)) {
        if (value.status === UPLOADING || value.status === QUEUED) pending++
        if (value.status === FAILED) failed++
      }
      if (pending > 0 || failed > 0) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [uploads])

  useEffect(() => {
    if (!fetched) requestUploads()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      requestHandleNewFiles(acceptedFiles)
    }
  }, [requestHandleNewFiles])

  const handleSave = async emailsList => {
    const ok = await requestUpdateNotifications(emailsList)
    if (ok) setNotifOpen(false)
  }

  const uploadsList = Object.values(uploads).map(value => (
    <S3UploaderProgress
      key={value.key}
      s3key={value.key}
      percentage={value.percentage}
      filename={value.name}
      totalBytes={value.totalBytesFriendly}
      uploadedBytes={value.uploadedBytesFriendly}
      timeLeftForUpload={value.timeLeftForUploadFriendly}
      totalUploadTime={value.uploadTime}
      status={value.status}
      abortCallback={value.abortUpload}
      errorMessage={value.errorMessage}
    />
  ))

  return (
    <div className="space-y-3">
      {hasError && <Alert variant="error">{error}</Alert>}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* ── Left: Recent Uploads ── */}
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <div>
              <h2 className="text-sm font-semibold leading-tight">Recent Uploads</h2>
              <p className="text-xs text-muted-foreground">
                Showing {recentFiles.length} most recently uploaded files
              </p>
            </div>
            <Button variant="outline" size="icon-sm" onClick={requestUploads} disabled={loading} title="Refresh list">
              {loading ? <Spinner size={16} /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>

          {recentFiles.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No files have been uploaded recently.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-[15%]">Size</TableHead>
                  <TableHead className="w-[32%]">Uploaded</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentFiles.map((file, i) => (
                  <TableRow key={i}>
                    <TableCell className="break-all">{file.name}</TableCell>
                    <TableCell>{file.fileSize}</TableCell>
                    <TableCell>
                      {`${new Date(file.createdDate).toLocaleDateString()} ${new Date(file.createdDate).toLocaleTimeString()}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* ── Right: Upload panel ── */}
        {accessToAtleastOneS3Uploader && (
          <Card className="p-4">
            <Alert variant="info" className="mb-4">
              Keep this page open until all uploads finish. Files can take up to a minute to appear in Recent Uploads.
            </Alert>

            {accessToMultipleS3Uploaders && (
              <div className="mb-4 space-y-1.5">
                <Label>Upload Account</Label>
                <Select
                  value={selectedUploadAccount.id || ''}
                  onValueChange={val => {
                    const account = uploadAccounts.find(a => a.id === val)
                    if (account) requestUploadAccountChange([account])
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an account" />
                  </SelectTrigger>
                  <SelectContent>
                    {uploadAccounts.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <UploadDropzone disabled={!selectedUploadAccount.rootpath} onDrop={onDrop} />

            <Separator className="my-4" />

            <div className={cn('flex flex-wrap items-center gap-2', uploadsList.length > 0 && 'mb-4')}>
              <Button variant="outline" size="sm" onClick={() => setNotifOpen(true)}>
                <Bell className="h-4 w-4" /> Notifications
              </Button>
              {uploadEmailNotificationsList && uploadEmailNotificationsList.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {uploadEmailNotificationsList.map(email => (
                    <Badge key={email} variant="outline">{email}</Badge>
                  ))}
                </div>
              )}
            </div>

            {uploadsList.length > 0 && (
              <div className="flex flex-col gap-3">{uploadsList}</div>
            )}
          </Card>
        )}
      </div>

      <NotificationsModal
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        initialEmails={uploadEmailNotificationsList}
        loading={uploadEmailNotificationsLoading}
        hasError={uploadEmailNotificationsHasError}
        error={uploadEmailNotificationsError}
        onSave={handleSave}
      />
    </div>
  )
}

const mapStateToProps = (state, route) => {
  const {
    fetched,
    recentFiles,
    uploads,
    uploadAccounts,
    selectedUploadAccount,
    accessToMultipleS3Uploaders,
    accessToAtleastOneS3Uploader,
    uploadEmailNotificationsList,
    uploadEmailNotificationsLoading,
    uploadEmailNotificationsHasError,
    uploadEmailNotificationsError,
    loading,
    error,
    hasError,
  } = state.stores.upload

  return {
    ...route.match.params,
    fetched,
    recentFiles,
    uploads,
    uploadAccounts,
    selectedUploadAccount,
    accessToMultipleS3Uploaders,
    accessToAtleastOneS3Uploader,
    uploadEmailNotificationsList,
    uploadEmailNotificationsLoading,
    uploadEmailNotificationsHasError,
    uploadEmailNotificationsError,
    loading,
    error,
    hasError,
  }
}

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators({
    requestUploads,
    requestUploadAccountChange,
    requestHandleNewFiles,
    requestUpdateNotifications,
  }, dispatch)
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Upload))
